import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import { IntlShape } from 'react-intl/lib';

import { onSyncEvent, onUpdateMeetingControl, onUpdateRoom } from '../../../../../graphql/subscriptions';
import { MeetingStatus } from '../../../../../api/meetings.dao';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { getComposedConnectionValues, getConnectionItemLabelText } from '../../../../../infrastructure/utils/connections.utils';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import {
    IControlDAO,
    IControlMeetingDetailsDTO,
    IControlRoomDTO,
    IMeetingAttendeeInput,
    IUpdateMeetingInput,
    IUpdateMeetingSubscriptionResponse,
    IUpdateRoomSubscriptionResponse,
    MeetingProgress,
    OperatorStatus,
    TCopyPodiumLinkFrom
} from '../../../../../api/control.dao';
import {
    APPSYNC_UPDATE_DEBOUNCE_TIME,
    CLONED_CONNECTION,
    ConnectionVersion,
    MAX_SAFE_TIMEOUT_DELAY,
    MEDIUM_PLAIN_TEXT_LENGTH,
    TIME_TO_HIGHLIGHT_INCOMPLETE_ATTENDEE
} from '../../../../../constants/common.constants';
import { ILocalStorageService } from '../../../../../infrastructure/local-storage-service';
import { AttendeeRole, IAttendeeDetailsDTO, IAttendeeInput, IAttendeeOrganizationDTO, IAttendeeOrganizationListDTO } from '../../../../../api/attendees.dao';
import { IOperatorDetailsDTO, IRoomsDAO, IUpdateRoomDTO } from '../../../../../api/rooms.dao';
import { IGNORE_ATTENDEES, IOrganizationsDAO } from '../../../../../api/organizations.dao';
import { getDisabled, getFilteredAttendees } from '../../../../../infrastructure/utils/attendee.utils';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { Space } from '../../../../../constants/html-character-entities';
import { debounce } from '../../../../../infrastructure/utils/debounce.utils';
import { ConnectionStatus, ConnectionType, IConnectionDetailsDTO, IConnectionGroupDTO, IDeleteConnectionRequestParams } from '../../../../../api/connections.dao';
import { ISubWindowFormMethods } from '../../manage-event.viewmodel';
import { IAppsyncSubscription, subscribeAppsyncData } from '../../../../../infrastructure/utils/appsync.utils';
import { ZoomMeetingVM } from '../../shared/connections-list/_zoom-meeting-modal/zoom-meeting.viewmodel';
import { WebinarReportsViewModel } from '../../shared/webinar-report/webinar-reports.viewmodel';
import { ISubWindowFacade } from '../../../../../infrastructure/sub-windows';
import { ControlFormDataVM } from './_control-form-data.viewmodel';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom, RUN_MEETING_EVENTS, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { IOnSyncEventSubscriptionResponse, OnSyncEventType } from '../../../../../api/events.dao';
import {
    EventDataSyncChangeset,
    MeetingChangesetMapper,
    MeetingDataSyncChangeset,
    MeetingRoomDataSyncChangeset
} from '../../../../../modules/event-sync-updates/meeting-changeset.mapper';
import { isBefore } from 'date-fns';
import { CreateZoomWebinarVM } from '../../shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar.viewmodel';
import { IOrganizationsForSpeakersSync } from '../../shared/connections-list/_connection-form/_sync-speakers/sync-speakers.viewmodel';
import { getOrganizationsForSpeakersSync } from '../../../../../infrastructure/utils/common.utils';
import { IDeleteConnectionDetails } from '../../shared/connections-list/_connection-delete-confirm-popover';
import { EventExecutionRuntime } from '../../../../../domain/jobs/implementations/event-execution-runtime';
import { errorText } from '../../../../../constants/common-labels.constants';
import { JobType } from '../../../../../api/jobs.dao';
import { IConfigurePassportDisplayFormProps } from '../../shared/configure-passport-display/configure-passport-display.viewmodel';
import { MeetingApiParams } from '../../shared/connections-list/_connection-form/connection-form.viewmodel';

const defaultValues: IControlMeetingDetailsDTO = {
    meetingId: null,
    progress: MeetingProgress.STANDBY,
    status: MeetingStatus.Scheduled,
    startDateTime: new Date(),
    room: {
        roomId: null,
        roomName: '',
        roomLabel: '',
        operators: [],
        connections: [],
        connectionGroups: [],
    },
    endDateTime: new Date(),
    hosts: [],
    presenters: [],
    participants: [],
    registrants: [],
    supports: [],
    operatorMeetingNotes: '',
    type: '',
    connections: [],
    connectionGroups: [],
    meetingConnectionNotes: '',
    meetingRecordEnabled: false,
    operatorSharedScreen: null,
    connectionVersion: null,
    operators: [],
    topic: '',
    podiumLinkEnabled: false,
};

export interface IControlFormProps {
    intl: IntlShape;
    accountId: Readonly<number>;
    eventId: Readonly<number>;
    meetingId: number;
    eventTimezone: string;
    isExpanded?: boolean;
    subWindowFormMethods: ISubWindowFormMethods;
    handleCloseSubWindow: (meetingId: number) => void;
    handleEditRoomClick: (roomId: number, path: OpenFrom) => void;
    handleEditAttendeeClick: (attendeeId: number, openFrom: OpenFrom) => void;
    updateMeetingsFullData: (meetings: IControlMeetingDetailsDTO[]) => void;
    meetingRunTime: number;
    showEventNotesPopup: (isEditMode: boolean, openFrom?: OpenFrom) => void;
    showConfigurePassportForm: (props: IConfigurePassportDisplayFormProps, connection: IConnectionDetailsDTO) => void;
    isEventNotesExist: boolean;
    guidelinesFormSubWindow: { [key: string]: ISubWindowFacade };
    handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    getPodiumLink: (params: MeetingApiParams) => Promise<string>;
    handlePodiumLinkFailure: (e: Error) => void;
    trackCopyPodiumLinkAction: (from: TCopyPodiumLinkFrom, meetingId: number) => void;
}

class ControlFormVM {
    private meetingSubscription: IAppsyncSubscription;
    private roomSubscription: IAppsyncSubscription;
    private eventDataSubscription: IAppsyncSubscription;

    @mobx.observable
    private zoomMeetingVM: ZoomMeetingVM;

    private webinarReportsVM: WebinarReportsViewModel;
    private webinarMeetingVM: CreateZoomWebinarVM;

    @mobx.observable
    public accountId: number;

    @mobx.observable
    public eventId: number;

    @mobx.observable
    public meetingId: number;

    @mobx.observable
    private eventTimezone: string;

    @mobx.observable
    public isExpanded: boolean;

    @mobx.observable
    public progress: MeetingProgress;

    @mobx.observable
    public controlFormDataVM: ControlFormDataVM;

    @mobx.observable
    private operatorMeetingNotes: string = '';

    @mobx.observable
    public meetingConnectionNotes: string = '';

    @mobx.observable
    public room: IControlRoomDTO;

    @mobx.observable
    public operators: IOperatorDetailsDTO;

    @mobx.observable
    public status: MeetingStatus;

    @mobx.observable
    public loading: boolean;

    @mobx.observable
    public selectedHosts: IAttendeeOrganizationDTO[] = [];

    @mobx.observable
    public selectedParticipants: IAttendeeOrganizationDTO[] = [];

    @mobx.observable
    public selectedPresenters: IAttendeeOrganizationDTO[] = [];

    @mobx.observable
    public selectedSupports: IAttendeeOrganizationDTO[] = [];

    @mobx.observable
    public attendees: IAttendeeOrganizationDTO[] = [];

    @mobx.observable
    public connections: IConnectionDetailsDTO[] = [];

    @mobx.observable
    public connectionGroups: IConnectionGroupDTO[] = [];

    @mobx.observable
    public defaultConnectionId: number | null = null;

    @mobx.observable
    public filterCriteria: string = '';

    @mobx.observable
    public attendeeIdToConnectionNameMap: { [id: string]: string } = {};

    @mobx.observable
    public orgIdToConnectionParticipantsNameMap: { [id: string]: string } = {};

    @mobx.observable
    public orgIdToConnectionPresentersNameMap: { [id: string]: string } = {};

    @mobx.observable
    public orgIdToConnectionHostsNameMap: { [id: string]: string } = {};
    @mobx.observable
    public orgIdToConnectionSupportsNameMap: { [id: string]: string } = {};

    @mobx.observable
    public meetingRecordEnabled: boolean;

    @mobx.observable
    public operatorSharedScreen: boolean | null;

    @mobx.observable
    public isMeetingUnderway: boolean;

    @mobx.observable
    public isEventNotesExist: boolean;

    @mobx.observable
    public guidelinesFormSubWindow: { [key: string]: ISubWindowFacade };

    @mobx.observable
    public organizationsForSpeakersSync: IOrganizationsForSpeakersSync;

    @mobx.observable
    public meetingConnectionVersion: ConnectionVersion;

    @mobx.observable
    public loadingAttendees = true;

    @mobx.observable
    public podiumLinkEnabled = false;

    private intl: IntlShape;

    private operatorsForUpdate: IOperatorDetailsDTO[] = [];
    private meetingOperatorsForUpdate: IOperatorDetailsDTO[] = [];
    private meetingConnectionsForUpdate: IConnectionDetailsDTO[] = [];
    private roomConnectionsForUpdate: IConnectionDetailsDTO[] = [];
    private attendeesForUpdate: IAttendeeInput[] = [];
    private walkInAttendeesForAdd: IMeetingAttendeeInput[] = [];
    private walkInAttendeesForRemove: IMeetingAttendeeInput[] = [];

    private updateRoomAfterDebounce: () => void;
    private updateMeetingAfterDebounce: (...args) => void;

    private roomRequestedTime: string;
    private meetingRequestedTime: string;

    public handleEditRoomClick: (roomId: number, path: OpenFrom) => void;
    public handleEditAttendeeClick: (attendeeId: number, openFrom: OpenFrom) => void;
    public handleCloseSubWindow: (meetingId: number) => void;
    public updateMeetingsFullData: (meetings: IControlMeetingDetailsDTO[]) => void;
    public showEventNotesPopup: (isEditMode: boolean, openFrom?: OpenFrom) => void;
    public showConfigurePassportForm: (props: IConfigurePassportDisplayFormProps, connection: IConnectionDetailsDTO) => void;
    public handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    public trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    public getPodiumLink: (params: MeetingApiParams) => Promise<string>;
    public handlePodiumLinkFailure: (error: Error) => void;
    public trackCopyPodiumLinkAction: (from: TCopyPodiumLinkFrom, meetingId: number) => void;

    private subWindowFormMethods: ISubWindowFormMethods;
    private timeout;

    constructor(
        @IoC.Inject private readonly controlDAO: IControlDAO = IoC.Container.get(IControlDAO),
        @IoC.Inject private readonly localeStorageService: ILocalStorageService = IoC.Container.get(ILocalStorageService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly organizationDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly roomsDAO: IRoomsDAO = IoC.Container.get(IRoomsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly executionRuntime: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
    ) {}

    public handleCloneConnection = (connection: IConnectionDetailsDTO) => {
        const localeStorageData = {
            name: CLONED_CONNECTION,
            value: connection,
            creationDate: Date.now()
        };
        this.localeStorageService.setLocalStorageValue(localeStorageData);
    };

    public async initialize(props: IControlFormProps) {
        const {
            intl,
            subWindowFormMethods,
            accountId,
            eventId,
            meetingId,
            eventTimezone,
            isExpanded = false,
            handleCloseSubWindow,
            handleEditRoomClick,
            handleEditAttendeeClick,
            updateMeetingsFullData,
            meetingRunTime,
            showEventNotesPopup,
            showConfigurePassportForm,
            isEventNotesExist,
            guidelinesFormSubWindow,
            handleTroubleshoot,
            trackHostJoinClick,
            getPodiumLink,
            handlePodiumLinkFailure,
            trackCopyPodiumLinkAction,
        } = props;

        this.intl = intl;
        this.accountId = accountId;
        this.eventId = eventId;
        this.meetingId = meetingId;
        this.eventTimezone = eventTimezone;
        this.isExpanded = isExpanded;
        this.subWindowFormMethods = subWindowFormMethods;
        this.handleCloseSubWindow = handleCloseSubWindow;
        this.handleEditRoomClick = handleEditRoomClick;
        this.handleEditAttendeeClick = (attendeeId: number, openFrom: OpenFrom) => handleEditAttendeeClick(attendeeId, openFrom);
        this.updateMeetingsFullData = updateMeetingsFullData;
        this.showEventNotesPopup = showEventNotesPopup;
        this.showConfigurePassportForm = showConfigurePassportForm;
        this.isEventNotesExist = isEventNotesExist;
        this.guidelinesFormSubWindow = guidelinesFormSubWindow;
        this.handleTroubleshoot = handleTroubleshoot;
        this.trackHostJoinClick = trackHostJoinClick;
        this.getPodiumLink = getPodiumLink;
        this.handlePodiumLinkFailure = handlePodiumLinkFailure;
        this.trackCopyPodiumLinkAction = trackCopyPodiumLinkAction;

        this.updateRoomAfterDebounce = debounce(this.updateRoomApiCall, APPSYNC_UPDATE_DEBOUNCE_TIME);
        this.updateMeetingAfterDebounce = debounce(this.updateMeetingApiCall, APPSYNC_UPDATE_DEBOUNCE_TIME);

        this.isMeetingUnderway = meetingRunTime >= TIME_TO_HIGHLIGHT_INCOMPLETE_ATTENDEE;
        const highlightDelay = TIME_TO_HIGHLIGHT_INCOMPLETE_ATTENDEE - meetingRunTime;
        if (!this.isMeetingUnderway && highlightDelay < MAX_SAFE_TIMEOUT_DELAY) {
            this.timeout = setTimeout(() => {
                mobx.runInAction(() => {
                    this.isMeetingUnderway = true;
                });
            }, highlightDelay);
        }

        this.subscribeMeeting();
        this.subscribeToEventUpdates();
        await this.fetchFormData();
        await this.fetchOrganizations(IGNORE_ATTENDEES);
    }

    public onUnmount = () => {
        this.meetingSubscription?.unsubscribe();
        this.roomSubscription?.unsubscribe();
        this.eventDataSubscription?.unsubscribe();
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    };

    public get isUniqueJoiningLinksDeletionTriggerEnabled(): boolean {
        return this.executionRuntime.getJobByType(JobType.DeleteUniqueJoiningLinks).triggerEnabled;
    }

    public initZoomMeetingVM() {
        this.zoomMeetingVM = new ZoomMeetingVM(this.eventTimezone, null, new Date(), new Date(), null,
            this.accountId, this.eventId, this.meetingId);
    }

    public initWebinarReportsVM() {
        this.webinarReportsVM = new WebinarReportsViewModel(this.accountId, this.eventId, OpenFrom.Control);
    }

    public getZoomMeetingVM(): ZoomMeetingVM {
        return this.zoomMeetingVM;
    }

    public initWebinarMeetingVM(): void {
        this.webinarMeetingVM = new CreateZoomWebinarVM(
            this.eventTimezone,
            '',
            new Date(),
            new Date(),
            null,
            { eventId: this.eventId, accountId: this.accountId }
        );
    }

    public getWebinarMeetingVM(): CreateZoomWebinarVM {
        return this.webinarMeetingVM;
    }

    public getWebinarReportsVM(): WebinarReportsViewModel {
        return this.webinarReportsVM;
    }

    @mobx.action
    private handleEventSubscriptionUpdates(meetingData: IOnSyncEventSubscriptionResponse): void {
        const { value: { data: { onSyncEvent: data } } } = meetingData;

        const meetingChangeset = MeetingChangesetMapper.syncedEventDataToMeetingChangeset(data);

        if (MeetingChangesetMapper.isMeetingSyncEvent(meetingChangeset.type)) {
            this.updateMeetingSubscriptionDetails(meetingChangeset as MeetingDataSyncChangeset);
        } else if (MeetingChangesetMapper.isMeetingRoomSyncEvent(meetingChangeset.type)) {
            this.updateMeetingRoomSubscriptionDetails(meetingChangeset as MeetingRoomDataSyncChangeset);
        } else if (MeetingChangesetMapper.isEventSyncEvent(meetingChangeset.type)) {
            this.updateEventSubscriptionDetails(meetingChangeset as EventDataSyncChangeset);
        }
    }

    @mobx.action
    private updateEventSubscriptionDetails(meetingChangeset: EventDataSyncChangeset): void {
        switch (meetingChangeset.type) {
            case OnSyncEventType.ATTENDEE_UPDATED:
                const details = meetingChangeset.applyChangesToMeeting({
                    participants: this.selectedParticipants,
                    presenters: this.selectedPresenters,
                    hosts: this.selectedHosts,
                    supports: this.selectedSupports
                });

                const [orgRole] = Object.keys(details);

                if (orgRole) this.setSyncedOrganizationsByRole(orgRole, details[orgRole]);

                break;
            default:
                break;
        }
    }

    @mobx.action
    private updateMeetingSubscriptionDetails(meetingChangeset: MeetingDataSyncChangeset): void {
        if (meetingChangeset.getMeetingId() !== this.meetingId) return;
        switch (meetingChangeset.type) {
            case OnSyncEventType.MEETING_DETAILS_UPDATED:
                const syncedDetails = meetingChangeset.applyChangesToMeeting({});
                this.setSyncedMeetingDetails(syncedDetails);
                break;

            case OnSyncEventType.MEETING_OPERATOR_ADDED:
            case OnSyncEventType.MEETING_OPERATOR_REMOVED:
            case OnSyncEventType.MEETING_OPERATOR_UPDATED:
                const syncedOperators = meetingChangeset.applyChangesToMeeting({ operators: [...this.controlFormDataVM.operators] });
                this.setSyncedMeetingOperators(syncedOperators);
                break;

            case OnSyncEventType.MEETING_ATTENDEE_UPDATED:
                const details = meetingChangeset.applyChangesToMeeting({
                    participants: this.selectedParticipants,
                    presenters: this.selectedPresenters,
                    hosts: this.selectedHosts,
                    supports: this.selectedSupports
                });

                const [orgRole] = Object.keys(details);

                if (orgRole) this.setSyncedOrganizationsByRole(orgRole, details[orgRole]);

                break;
            default:
                break;
        }
    }
    @mobx.action
    private updateMeetingRoomSubscriptionDetails(meetingChangeset: MeetingRoomDataSyncChangeset): void {
        if (this.room && Number(this.room.roomId) === meetingChangeset.getMeetingRoomId()) {
            switch (meetingChangeset.type) {
                case OnSyncEventType.ROOM_OPERATOR_ADDED:
                case OnSyncEventType.ROOM_OPERATOR_REMOVED:
                case OnSyncEventType.ROOM_OPERATOR_UPDATED:
                    const syncedRoomOperators = meetingChangeset.applyChangesToMeeting({
                        room: { operators: [...this.room.operators] }
                    } as Partial<IControlMeetingDetailsDTO>);
                    this.setSyncedMeetingRoomOperators(syncedRoomOperators);
                    break;
                default:
                    break;
            }
        }
    }

    @mobx.action
    private setSyncedOrganizationsByRole(role: string, organizations: IAttendeeOrganizationDTO[]): void {
        switch (role) {
            case 'hosts':
                this.selectedHosts = organizations;
                break;
            case 'participants':
                this.selectedParticipants = organizations;
                break;
            case 'presenters':
                this.selectedPresenters = organizations;
                break;
            case 'supports':
                this.selectedSupports = organizations;
                break;
            default:
                break;
        }
    }

    @mobx.action
    private setSyncedMeetingDetails(details: Partial<IControlMeetingDetailsDTO>): void {
        this.progress = details.progress;
        this.status = details.status;
        this.operatorMeetingNotes = details.operatorMeetingNotes;
        this.operatorSharedScreen = details.operatorSharedScreen ?? null;
    }

    @mobx.action
    private setSyncedMeetingOperators(details: Partial<IControlMeetingDetailsDTO>): void {
        this.controlFormDataVM.operators = details.operators;
    }
    @mobx.action
    private setSyncedMeetingRoomOperators(details: Partial<IControlMeetingDetailsDTO>): void {
        this.room.operators = details.room.operators;
    }

    private async subscribeToEventUpdates(): Promise<void> {
        this.eventDataSubscription = await subscribeAppsyncData<IOnSyncEventSubscriptionResponse>(onSyncEvent, { eventId: this.eventId },
            data => this.handleEventSubscriptionUpdates(data),
            () => this.subscribeToEventUpdates()
        );
    }

    private subscribeMeeting = async () => {
        this.meetingSubscription = await subscribeAppsyncData<IUpdateMeetingSubscriptionResponse>(onUpdateMeetingControl, { meetingId: this.meetingId },
            data => {
                const { value: { data: { onUpdateMeetingControl: {
                    participants,
                    presenters,
                    hosts,
                    supports,
                    requestedTime,
                    connections
                }}}} = data;
                mobx.runInAction(() => {
                    // ignore subscription if current client invoke this subscription
                    if (!requestedTime || requestedTime !== this.meetingRequestedTime) {
                        this.selectedParticipants = participants;
                        this.selectedPresenters = presenters;
                        this.selectedHosts = hosts;
                        this.selectedSupports = supports;
                        this.connections = connections.map(connection => getComposedConnectionValues(this.connections, connection));
                    }
                });
            },
            async () => {
                await this.fetchFormData();
                await this.fetchOrganizations(IGNORE_ATTENDEES);
                this.subscribeMeeting();
            }
        );
    };

    private subscribeRoom = async () => {
        this.roomSubscription = await subscribeAppsyncData<IUpdateRoomSubscriptionResponse>(onUpdateRoom, { roomId: this.room.roomId },
            data => {
                const { value: { data: { onUpdateRoom: { requestedTime, ...room }}}} = data;
                // ignore subscription if current client invoke this subscription
                if (!requestedTime || requestedTime !== this.roomRequestedTime) {
                    mobx.runInAction(() => {
                        this.room = {
                            ...room,
                            operators: this.room.operators,
                            connections: room.connections.map(connection => getComposedConnectionValues(this.room.connections, connection))
                        };
                    });
                }
            },
            this.subscribeRoom
        );
    };

    public getMeetingNote = () => {
        return this.operatorMeetingNotes;
    };

    public getEditableTextareaErrorMessage = (content: string) => {
        if (content.length > MEDIUM_PLAIN_TEXT_LENGTH) {
            return (
                <FormattedMessage
                    defaultMessage='The notes is too long. Max length is {length} characters.'
                    id='oe.error.noteIsTooLong'
                    description='Error message text'
                    values={{
                        length: MEDIUM_PLAIN_TEXT_LENGTH
                    }}
                />
            );
        }
        return null;
    };

    public getDisabledAttendees = () => {
        return getDisabled([...this.selectedHosts, ...this.selectedPresenters, ...this.selectedParticipants, ...this.selectedSupports]);
    };

    public getMeetingProgress() {
        return this.progress;
    }

    public async fetchOrganizations(ignoreAttendees: boolean): Promise<IAttendeeOrganizationListDTO> {
        let organizationsList: IAttendeeOrganizationListDTO = { content: [], totalElements: 0 };

        try {
            organizationsList = await this.organizationDAO.getAllOrganizations({
                eventId: this.eventId,
                accountId: this.accountId,
                ignoreAttendees: ignoreAttendees
            });

            mobx.runInAction(() => {
                this.attendees = organizationsList.content;
            });
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
        }

        return organizationsList;
    }

    @mobx.action
    public async callFetchOrganizationsFromPopUp(): Promise<void> {
        this.loadingAttendees = true;

        await this.fetchOrganizations(!IGNORE_ATTENDEES);

        mobx.runInAction(() => {
            this.loadingAttendees = false;
        });
    }

    @mobx.action
    public async fetchFormData(shouldUpdateMeetingInList?: boolean) {
        this.loading = true;
        try {
            const meetingDetails: IControlMeetingDetailsDTO = await this.controlDAO.getControlEventMeeting({
                meetingId: this.meetingId,
                eventId: this.eventId,
                accountId: this.accountId,
                ignoreAttendees: IGNORE_ATTENDEES
            });

            if (shouldUpdateMeetingInList) {
                this.updateMeetingsFullData([meetingDetails]);
            }
            this.handleControlMeetingLoaded(meetingDetails);
        } catch (e) {
            this.handleCloseSubWindow(this.meetingId);
            alertServerError(errorText(), this.errorParsingService.extractMessage(e));
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    }

    public fetchAttendeeData = async (attendeeId: number) => {
        return await this.controlDAO.getControlAttendee({
            eventId: this.eventId,
            accountId: this.accountId,
            attendeeId: attendeeId
        });
    };

    @mobx.action
    public updateAttendee = (
        attendeeInput: IAttendeeInput,
        roleKey: string,
        organizationId: number,
        attendeeId: number
    ) => {
        // update local attendee in organizations observable array
        const updatedOrganization: IAttendeeOrganizationDTO = this[roleKey].find(
            ({ organizationId: id }) => id === organizationId
        );
        const updatedAttendee = updatedOrganization.attendees.find(attendee => attendee.attendeeId === attendeeId);
        const updatedAttendeeIndex = updatedOrganization.attendees.indexOf(updatedAttendee);
        updatedOrganization.attendees = [...updatedOrganization.attendees.filter(el => el !== updatedAttendee)];
        updatedOrganization.attendees.splice(updatedAttendeeIndex, 0, {
            // push attendee at index with old data + changed fields
            ...updatedAttendee,
            ...attendeeInput
        });

        // create array of changed attendees for Api request
        const existedAttendee = this.attendeesForUpdate.find(
            el => el.meetingAttendeeId === attendeeInput.meetingAttendeeId
        );
        if (existedAttendee) {
            const existedAttendeeIndex = this.attendeesForUpdate.indexOf(existedAttendee);
            this.attendeesForUpdate[existedAttendeeIndex] = attendeeInput;
        } else {
            this.attendeesForUpdate.push(attendeeInput);
        }

        this.updateMeetingAfterDebounce();
    };

    @mobx.action
    public updateMeetingProgress = (newProgress: MeetingProgress) => {
        this.progress = newProgress;

        this.updateMeetingAfterDebounce(true, RUN_MEETING_EVENTS.STATUS_UPDATE);
    };

    @mobx.action
    public setFilterCriteria = (value: string) => {
        this.filterCriteria = value;
        this.trackEventMixPanel(RUN_MEETING_EVENTS.ATTENDEES_SEARCH);
    };

    @mobx.action
    public removeWalkIn = (roleKey: string, role: AttendeeRole, organizationId: number, attendeeId: number) => {
        this[roleKey] = this[roleKey].reduce((organizations, organization) => {
            if (organization.organizationId === organizationId) {
                organization.attendees = organization.attendees.filter(attendee => attendee.attendeeId !== attendeeId);
            }
            if (organization.attendees.length) {
                organizations.push(organization);
            }
            return organizations;
        }, []);

        const removeAttendeeInput: IMeetingAttendeeInput = {
            organizationId: organizationId.toString(),
            role: role,
            attendeeIds: [attendeeId]
        };

        this.walkInAttendeesForRemove.push(removeAttendeeInput);
        this.updateMeetingAfterDebounce();
    };

    @mobx.action
    public addWalkInAttendees = (walkInsInput: IMeetingAttendeeInput[]) => {
        this.walkInAttendeesForAdd = walkInsInput;
        let trackingEvent: RUN_MEETING_EVENTS;
        switch (walkInsInput[0]?.role) {
            case AttendeeRole.Host:
                trackingEvent = RUN_MEETING_EVENTS.ADD_WALK_IN_HOST;
                break;
            case AttendeeRole.Participant:
                trackingEvent = RUN_MEETING_EVENTS.ADD_WALK_IN_PARTICIPANT;
                break;
            case AttendeeRole.Presenter:
                trackingEvent = RUN_MEETING_EVENTS.ADD_WALK_IN_PRESENTER;
                break;
        }

        this.updateMeetingApiCall(false, trackingEvent);
    };

    @mobx.action
    public updateOperatorMeetingNotes = (newNotes: string) => {
        this.operatorMeetingNotes = newNotes;

        this.updateMeetingApiCall(true, RUN_MEETING_EVENTS.OPERATOR_MEETING_NOTES);
    };

    @mobx.action
    public updateOperatorSharedScreen = async (value: boolean | null): Promise<void> => {
        this.operatorSharedScreen = value;
        await this.updateMeetingApiCall();
        this.trackEventMixPanel(
            RUN_MEETING_EVENTS.OPERATOR_SHARED_SCREEN,
            value === true ? 'Yes' : value === false ? 'No' : 'Not Specified'
        );
    };

    @mobx.action
    public getFiltered = (attendees: IAttendeeOrganizationDTO[]) =>
        getFilteredAttendees(attendees, this.filterCriteria, true, true, true);

    public editMeeting = () => {
        this.subWindowFormMethods.schedule.openEditSubWindow(this.meetingId, OpenFrom.RunMeeting);
    };

    public editMeetingConnections = () => {
        this.subWindowFormMethods.meetingConnections.openEditSubWindow(this.meetingId, OpenFrom.RunMeeting);
    };

    public updateMeetingApiCall = async (withRequestedTime = true, trackingEvent?: RUN_MEETING_EVENTS) => {
        this.meetingRequestedTime = Date.now().toString();

        const meetingData: IUpdateMeetingInput = {
            accountId: this.accountId,
            eventId: this.eventId,
            meetingId: this.meetingId,
            progress: this.progress,
            operatorMeetingNotes: this.operatorMeetingNotes,
            status: this.status,
            operatorSharedScreen: this.operatorSharedScreen,
            ignoreAttendees: !IGNORE_ATTENDEES
        };
        // request body for update meeting in NRT and then clear variables
        if (this.meetingOperatorsForUpdate.length) meetingData.meetingOperators = this.meetingOperatorsForUpdate;
        if (this.meetingConnectionsForUpdate.length) meetingData.meetingConnections = this.meetingConnectionsForUpdate;
        if (this.attendeesForUpdate.length) meetingData.attendees = this.attendeesForUpdate;
        if (this.walkInAttendeesForAdd.length) meetingData.addMeetingAttendees = this.walkInAttendeesForAdd;
        if (this.walkInAttendeesForRemove.length) meetingData.removeMeetingAttendees = this.walkInAttendeesForRemove;
        if (withRequestedTime) meetingData.requestedTime = this.meetingRequestedTime;

        this.meetingOperatorsForUpdate = [];
        this.attendeesForUpdate = [];
        this.walkInAttendeesForAdd = [];
        this.walkInAttendeesForRemove = [];
        this.meetingConnectionsForUpdate = [];

        try {
            await this.controlDAO.updateControlEventMeeting(meetingData);
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
        }

        if (trackingEvent) {
            this.trackEventMixPanel(trackingEvent);
        }
    };

    @mobx.action
    public updateRoom = async (updatedOperator: IOperatorDetailsDTO) => {
        const existedOperator = this.operatorsForUpdate.find(el => el.operatorId === updatedOperator.operatorId);

        if (existedOperator) {
            const indexOfExistedOperator = this.operatorsForUpdate.indexOf(existedOperator);
            this.operatorsForUpdate[indexOfExistedOperator] = updatedOperator;
        } else {
            this.operatorsForUpdate.push(updatedOperator);
        }

        this.updateRoomAfterDebounce();
    };

    public updateMeetingOperators = (updatedOperator?: IOperatorDetailsDTO) => {
        if (updatedOperator) {
            const existedOperator = this.meetingOperatorsForUpdate.find(el => el.operatorId === updatedOperator.operatorId);

            if (existedOperator) {
                const indexOfExistedOperator = this.meetingOperatorsForUpdate.indexOf(existedOperator);
                this.meetingOperatorsForUpdate[indexOfExistedOperator] = updatedOperator;
            } else {
                this.meetingOperatorsForUpdate.push(updatedOperator);
            }
        }

        this.updateMeetingAfterDebounce();
    };

    public updateMeetingConnections = (updatedConnection?: IConnectionDetailsDTO) => {
        if (updatedConnection) {
            const existedConnection = this.meetingConnectionsForUpdate.find(el => el.meetingConnectionId === updatedConnection.meetingConnectionId);
            if (existedConnection) {
                const indexOfExistedConnection = this.meetingConnectionsForUpdate.indexOf(existedConnection);
                this.meetingConnectionsForUpdate[indexOfExistedConnection] = updatedConnection;
            } else {
                this.meetingConnectionsForUpdate.push(updatedConnection);
            }
        }
        this.updateMeetingAfterDebounce();
    };

    public updateRoomConnections = (updatedConnection?: IConnectionDetailsDTO) => {
        if (updatedConnection) {
            const existedConnection = this.roomConnectionsForUpdate.find(el => el.meetingConnectionId === updatedConnection.meetingConnectionId);
            if (existedConnection) {
                const indexOfExistedConnection = this.roomConnectionsForUpdate.indexOf(existedConnection);
                this.roomConnectionsForUpdate[indexOfExistedConnection] = updatedConnection;
            } else {
                this.roomConnectionsForUpdate.push(updatedConnection);
            }
        }
        this.updateRoomAfterDebounce();
    };

    public updateRoomApiCall = async () => {
        this.roomRequestedTime = Date.now().toString();

        const roomData: IUpdateRoomDTO = {
            accountId: this.accountId,
            eventId: this.eventId,
            roomId: this.room.roomId,
            requestedTime: this.roomRequestedTime
        };
        if (this.operatorsForUpdate.length) roomData.roomOperators = this.operatorsForUpdate;
        if (this.roomConnectionsForUpdate.length) roomData.meetingConnections = this.roomConnectionsForUpdate;

        this.operatorsForUpdate = [];
        this.roomConnectionsForUpdate = [];

        try {
            await this.roomsDAO.updateRoomInEvent(roomData);
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
        }
    };

    private setConnectionsMap({
        defaultConnectionId: defaultMeetingConnectionId,
        endDateTime,
        presenterRoleConnectionId,
        hostRoleConnectionId,
        supportRoleConnectionId
    }: IControlMeetingDetailsDTO): void {
        const attendeeIdToConnectionNameMap = {};
        const orgIdToConnectionParticipantsNameMap = {};
        const orgIdToConnectionPresentersNameMap = {};
        const orgIdToConnectionHostsNameMap = {};
        const orgIdToConnectionSupportsNameMap = {};
        const defaultRoomConnectionId = this.room.defaultConnection?.meetingConnectionId;
        const defaultConnectionId = defaultMeetingConnectionId || defaultRoomConnectionId;
        const presentersConnectionId = presenterRoleConnectionId
            || this.room.presentersConnection?.meetingConnectionId
            || defaultConnectionId;
        const hostsConnectionId = hostRoleConnectionId
            || this.room.hostsConnection?.meetingConnectionId
            || defaultConnectionId;
        const supportsConnectionId = supportRoleConnectionId || defaultConnectionId;
        const knovioReplayConnectionId = this.getKnovioReplayConnection(this.connections, endDateTime)?.meetingConnectionId;
        const connectionsIdToNameMap = [...this.connections, ...this.room.connections, ...this.room.connectionGroups, ...this.connectionGroups].reduce(
            (acc: { [id: string]: string }, c) => ({
                ...acc,
                [c.meetingConnectionId]: getConnectionItemLabelText(c)
            }),
            {}
        );

        this.selectedParticipants.forEach(
            ({ attendees, meetingConnectionId: orgConnectionId, organizationId }) => {
                orgIdToConnectionParticipantsNameMap[organizationId] =
                    connectionsIdToNameMap[knovioReplayConnectionId || orgConnectionId || defaultConnectionId];
                attendees.forEach(
                    ({ meetingConnectionId: attendeeConnectionId, attendeeId }) =>
                    (attendeeIdToConnectionNameMap[attendeeId] =
                        connectionsIdToNameMap[knovioReplayConnectionId || attendeeConnectionId || orgConnectionId || defaultConnectionId])
                );
            }
        );

        this.selectedHosts.forEach(({ attendees, meetingConnectionId: orgConnectionId, organizationId }) => {
            orgIdToConnectionHostsNameMap[organizationId] =
                connectionsIdToNameMap[knovioReplayConnectionId || orgConnectionId || hostsConnectionId];
            attendees.forEach(
                ({ meetingConnectionId: attendeeConnectionId, attendeeId }) =>
                (attendeeIdToConnectionNameMap[attendeeId] =
                    connectionsIdToNameMap[knovioReplayConnectionId || attendeeConnectionId || orgConnectionId || hostsConnectionId])
            );
        });

        this.selectedPresenters.forEach(({ attendees, meetingConnectionId: orgConnectionId, organizationId }) => {
            orgIdToConnectionPresentersNameMap[organizationId] =
                connectionsIdToNameMap[knovioReplayConnectionId || orgConnectionId || presentersConnectionId];
            attendees.forEach(
                ({ meetingConnectionId: attendeeConnectionId, attendeeId }) =>
                (attendeeIdToConnectionNameMap[attendeeId] =
                    connectionsIdToNameMap[knovioReplayConnectionId || attendeeConnectionId || orgConnectionId || presentersConnectionId])
            );
        });

        this.selectedSupports.forEach(({ attendees, meetingConnectionId: orgConnectionId, organizationId }) => {
            orgIdToConnectionSupportsNameMap[organizationId] =
                connectionsIdToNameMap[knovioReplayConnectionId || orgConnectionId || supportsConnectionId];
            attendees.forEach(
                ({ meetingConnectionId: attendeeConnectionId, attendeeId }) =>
                (attendeeIdToConnectionNameMap[attendeeId] =
                    connectionsIdToNameMap[knovioReplayConnectionId || attendeeConnectionId || orgConnectionId || supportsConnectionId])
            );
        });

        this.attendeeIdToConnectionNameMap = attendeeIdToConnectionNameMap;
        this.orgIdToConnectionParticipantsNameMap = orgIdToConnectionParticipantsNameMap;
        this.orgIdToConnectionPresentersNameMap = orgIdToConnectionPresentersNameMap;
        this.orgIdToConnectionHostsNameMap = orgIdToConnectionHostsNameMap;
        this.orgIdToConnectionSupportsNameMap = orgIdToConnectionSupportsNameMap;
    }

    @mobx.action
    private handleControlMeetingLoaded(meetingDetails: IControlMeetingDetailsDTO) {
        this.progress = meetingDetails.progress || defaultValues.progress;
        this.connections = meetingDetails.connections || defaultValues.connections;
        this.connectionGroups = meetingDetails.connectionGroups || defaultValues.connectionGroups;
        this.operatorMeetingNotes = meetingDetails.operatorMeetingNotes || defaultValues.operatorMeetingNotes;
        this.room = meetingDetails.room || defaultValues.room;
        this.controlFormDataVM = new ControlFormDataVM(meetingDetails);
        this.selectedParticipants = meetingDetails?.participants || defaultValues.participants;
        this.selectedPresenters = meetingDetails?.presenters || defaultValues.presenters;
        this.selectedHosts = meetingDetails?.hosts || defaultValues.hosts;
        this.selectedSupports = meetingDetails?.supports || defaultValues.supports;
        this.status = meetingDetails?.status || defaultValues.status;
        this.meetingRecordEnabled = meetingDetails.meetingRecordEnabled || defaultValues.meetingRecordEnabled;
        this.operatorSharedScreen = meetingDetails.operatorSharedScreen ?? null;
        this.defaultConnectionId = meetingDetails?.defaultConnectionId;
        this.organizationsForSpeakersSync = getOrganizationsForSpeakersSync(meetingDetails);
        this.meetingConnectionVersion = meetingDetails.connectionVersion || defaultValues.connectionVersion;
        this.podiumLinkEnabled = meetingDetails.podiumLinkEnabled || defaultValues.podiumLinkEnabled;

        this.setConnectionsMap(meetingDetails);
        if (this.room?.roomId) this.subscribeRoom();
    }

    @mobx.action
    public updateOperatorStatus(email: string) {
        const updatedOperator = this.room.operators.find(operator => operator.email === email);
        const operatorIndex = this.room.operators.indexOf(updatedOperator);
        const newStatus =
            updatedOperator.operatorStatus === OperatorStatus.READY ? OperatorStatus.NOT_READY : OperatorStatus.READY;
        const tempOperators = this.room.operators.filter(operator => operator.email !== email);
        const newOperator = { ...updatedOperator, operatorStatus: newStatus };
        tempOperators.splice(operatorIndex, 0, newOperator);
        this.room = { ...this.room, operators: tempOperators };
        this.updateRoom(newOperator);
    }

    @mobx.action
    public updateRoomConnectionStatus(meetingConnectionId: number) {
        const updatedConnection = this.room.connections.find(connection => connection.meetingConnectionId === meetingConnectionId);
        const connectionIndex = this.room.connections.indexOf(updatedConnection);
        const newStatus =
            updatedConnection.connectionStatus === ConnectionStatus.STARTED ? ConnectionStatus.NOT_STARTED : ConnectionStatus.STARTED;
        const tempConnections = this.room.connections.filter(connection => connection.meetingConnectionId !== meetingConnectionId);
        const newConnection = { ...updatedConnection, connectionStatus: newStatus };
        tempConnections.splice(connectionIndex, 0, newConnection);
        this.room = { ...this.room, connections: tempConnections };
        this.updateRoomConnections(newConnection);
    }

    @mobx.action
    public updateMeetingConnectionStatus(meetingConnectionId: number) {
        const updatedConnection = this.connections.find(connection => connection.meetingConnectionId === meetingConnectionId);
        const connectionIndex = this.connections.indexOf(updatedConnection);
        const newStatus =
            updatedConnection.connectionStatus === ConnectionStatus.STARTED ? ConnectionStatus.NOT_STARTED : ConnectionStatus.STARTED;
        const tempConnections = this.connections.filter(connection => connection.meetingConnectionId !== meetingConnectionId);
        const newConnection = { ...updatedConnection, connectionStatus: newStatus };
        tempConnections.splice(connectionIndex, 0, newConnection);
        this.connections = tempConnections;
        this.updateMeetingConnections(newConnection);
    }

    @mobx.action
    public updateMeetingOperatorStatus(email: string) {
        const updatedOperator = this.controlFormDataVM.operators.find(operator => operator.email === email);
        const operatorIndex = this.controlFormDataVM.operators.indexOf(updatedOperator);
        const newStatus = updatedOperator.operatorStatus === OperatorStatus.READY ? OperatorStatus.NOT_READY : OperatorStatus.READY;
        const tempOperators = this.controlFormDataVM.operators.filter(operator => operator.email !== email);
        const newOperator = { ...updatedOperator, operatorStatus: newStatus };
        tempOperators.splice(operatorIndex, 0, newOperator);
        this.controlFormDataVM = { ...this.controlFormDataVM, operators: tempOperators };
        this.updateMeetingOperators(newOperator);
    }

    public cancelMeeting = () => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.alerts.areYouSure'
                        description='Alert message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.alerts.cancelThisMeeting'
                            description='Alert message'
                            defaultMessage='cancel this meeting'
                        />
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.meetings.cancelMeeting'
                        description='Button label'
                        defaultMessage='Cancel Meeting'
                    />
                ),
                modifier: 'confirm-warn',
                handler: this.cancelMeetingApiCall
            },
            cancelButtonProps: {
                text: (
                    <FormattedMessage id='oe.common.noGoBack' description='Button label' defaultMessage='No, go back' />
                ),
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    private cancelMeetingApiCall = async () => {
        this.status = MeetingStatus.Cancelled;
        this.updateMeetingApiCall(true, RUN_MEETING_EVENTS.CANCEL_MEETING);
    };

    public getControlFormDataVM(): ControlFormDataVM {
        return this.controlFormDataVM;
    }

    public getEventTimeZone = () => {
        return this.eventTimezone;
    };

    public getIsPageReady() {
        return !this.loading;
    }

    public getMeetingId = () => {
        return this.meetingId;
    };

    public getAccountId = () => {
        return this.accountId;
    };

    public getEventId = () => {
        return this.eventId;
    };

    @mobx.action
    public createAttendeeFromForm = (role: AttendeeRole) => {
        this.subWindowFormMethods.attendees.openNewSubWindow(OpenFrom.Control, async (newAttendee: IAttendeeDetailsDTO) => {
            const updatedOrganization = await this.fetchOrganizations(IGNORE_ATTENDEES);
            const org = updatedOrganization.content.find(organization => {
                return organization.organizationId === newAttendee.organizationId;
            });
            const walkInToAPI: IMeetingAttendeeInput = {
                organizationId: org.organizationId.toString(),
                role: role,
                attendeeIds: [newAttendee.attendeeId]
            };
            this.addWalkInAttendees([walkInToAPI]);
        });
    };

    public onEditConnection = (connection: IConnectionDetailsDTO) => {
        const isRoomConnection = this.room.connections.find(
            ({ meetingConnectionId }) => meetingConnectionId === connection.meetingConnectionId);
        const isEdit = this.zoomMeetingVM.getEditModeStatus();

        if (connection.type === ConnectionType.Zoom && !isEdit) {
            this.zoomMeetingVM.getZoomDetails(connection);
            this.zoomMeetingVM.setUpdateZoomMeetingCallback((data: IConnectionDetailsDTO) => {
                this.updateZoomMeetingCallback(
                    // manually assign blueprintId to the connection fetched from the zoom
                    {...data, blueprintId: connection.blueprintId}
                );
            });
            return;
        }

        if (connection.type === ConnectionType.Webinar && !this.webinarMeetingVM.isEditMode) {
            this.webinarMeetingVM.openEditWebinarModal(connection);
            this.webinarMeetingVM.setUpdateWebinarMeetingCallback((data: IConnectionDetailsDTO) => {
                this.updateWebinarMeetingCallback(data);
            });
            return;
        }

        if (isRoomConnection) {
            return this.subWindowFormMethods.roomConnectionDetails.openEditSubWindow(this.room.roomId, connection, () => this.fetchFormData(true), false);
        }
        let shouldShowKnovioReplayToggle = false;
        if (connection.type === ConnectionType.Knovio || connection.type === ConnectionType.Other) {
            const knovioReplayConnection = this.connections.find(c => c.passportReplayEnabled);
            shouldShowKnovioReplayToggle =
                !knovioReplayConnection
                || connection.meetingConnectionId === knovioReplayConnection.meetingConnectionId
            ;
        }
        return this.subWindowFormMethods.meetingConnectionDetails.openEditSubWindow(
            this.meetingId,
            connection,
            () => this.fetchFormData(true),
            false,
            shouldShowKnovioReplayToggle,
            {organizationsForSpeakersSync: this.organizationsForSpeakersSync}
        );
    };

    public updateZoomMeetingCallback = (zoomMeetingDetails?: IConnectionDetailsDTO) => {
        const meetingTopic = this.zoomMeetingVM.createZoomMeetingFormDataVM.getMeetingTopic();
        if (meetingTopic) {
            zoomMeetingDetails.connectionTopic = meetingTopic;
        }
        this.onEditConnection(zoomMeetingDetails);
    };

    public updateWebinarMeetingCallback = (webinarDetails: IConnectionDetailsDTO) => {
        this.onEditConnection(webinarDetails);
    };

    public onDeleteConnection = (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails ) => {
        const isRoomConnection = this.room.connections.find(connection => connection.meetingConnectionId === data.meetingConnectionId
        );

        isRoomConnection
            ? this.subWindowFormMethods.roomConnectionDetails.openDeleteSubWindow(this.room.roomId, data, () =>
                this.fetchFormData(true)
            )
            : this.subWindowFormMethods.meetingConnectionDetails.openDeleteSubWindow(this.meetingId, data, () =>
                this.fetchFormData(true), connectionDetails
            );
    };

    public viewEventGuidelines = (): void => {
        this.showEventNotesPopup(false, OpenFrom.RunMeeting);
        this.trackEventMixPanel(RUN_MEETING_EVENTS.VIEW_EVENT_GUIDELINES);
    };

    public trackEventMixPanel = (eventName: RUN_MEETING_EVENTS, value?: string): void => {
        this.trackingService.get().track(TRACKING_EVENTS.RUN_MEETING_ACTIONS, {
            [eventName]: value || 'Yes'
        });
    };

    public getKnovioReplayConnection = (connections: IConnectionDetailsDTO[], endDateTime: Date): IConnectionDetailsDTO | undefined => {
        const now = new Date();
        const endTime = new Date(endDateTime);
        return isBefore(endTime, now)
            ? connections.find(c => (c.type === ConnectionType.Knovio || c.type === ConnectionType.Other) && c.passportReplayEnabled)
            : undefined
        ;
    };

    public trackFormOpenEventMixPanel = (eventName: string): void => {
        this.trackingService.get().track(eventName,{
            [eventName]: 'Yes'
        });
    };

    public shouldDisableDeleteButton = (connection: IConnectionDetailsDTO): boolean => {
        return connection?.autoSyncEnabled && !this.executionRuntime.isReady();
    };

    @mobx.action
    public resetLoadingState = (): void => {
        this.loadingAttendees = true;
    };
}

export { ControlFormVM };